import { Link } from '@tanstack/react-router';
import { useEffect } from 'react';

import { SyncStatus } from '../components/SyncStatus.jsx';
import { APP_VERSION } from '../config.js';
import { useDevHandles } from '../db/dev-handles.js';
import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';
import { useSession, type SessionUser } from '../session.jsx';
import { ThemeControl } from '../theme/ThemeControl.jsx';

/**
 * Bottom of the sidebar: capture and command palette entries, sync state (docs/05 §6.3), appearance, account,
 * and the F0 test screens that stay reachable until their real screens arrive.
 */
export function SidebarFooter({
  user,
  local,
  onOpenPalette,
  onOpenCapture,
}: {
  user: SessionUser;
  local: LocalDatabase;
  onOpenPalette: () => void;
  onOpenCapture: () => void;
}) {
  const { signOut } = useSession();
  return (
    <>
      <button type="button" className="sidebar__command" onClick={onOpenCapture}>
        <span>{t.quickCapture.open}</span>
        <kbd>N</kbd>
      </button>
      <button type="button" className="sidebar__command" onClick={onOpenPalette}>
        <span>{t.shell.openPalette}</span>
        <kbd>⌘K</kbd>
      </button>
      <nav className="sidebar__tests" aria-label={t.shell.testScreens}>
        <p className="sidebar__section-title">{t.shell.testScreens}</p>
        <Link to="/blocks">{t.nav.blocksScreen}</Link>
        <Link to="/capture">{t.nav.capture}</Link>
        <Link to="/search">{t.nav.search}</Link>
        <Link to="/perf">{t.nav.perf}</Link>
      </nav>
      <SyncStatus local={local} />
      <ThemeControl local={local} userId={user.id} />
      <div className="sidebar__account">
        <span className="muted">{t.session.signedInAs(user.email)}</span>
        <button type="button" onClick={() => void signOut()}>
          {t.session.signOut}
        </button>
        <span className="muted" data-testid="app-version">
          v{APP_VERSION}
        </span>
      </div>
      <DbHandle local={local} userId={user.id} />
    </>
  );
}

/** Exposes the open database for the perf and e2e tooling (apps/web/perf, apps/web/e2e). */
export function DbHandle({ local, userId }: { local: LocalDatabase; userId: string }) {
  useDevHandles(local, userId);
  useEffect(() => {
    window.__flowDb = local.db;
    return () => {
      delete window.__flowDb;
    };
  }, [local.db]);
  return null;
}
